import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import * as api from './contentPlannerAPI.js';

// Async thunks

export const fetchNichePresets = createAsyncThunk('contentPlanner/fetchNichePresets', async (_, { rejectWithValue }) => {
  try {
    const res = await api.getNichePresets();
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const fetchStrategies = createAsyncThunk('contentPlanner/fetchStrategies', async ({ profileId }, { rejectWithValue }) => {
  try {
    const res = await api.listStrategies(profileId);
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const createStrategy = createAsyncThunk('contentPlanner/createStrategy', async ({ profileId, data }, { rejectWithValue }) => {
  try {
    const res = await api.createStrategy(profileId, data);
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const updateStrategy = createAsyncThunk('contentPlanner/updateStrategy', async ({ strategyId, data }, { rejectWithValue }) => {
  try {
    const res = await api.updateStrategy(strategyId, data);
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const deleteStrategy = createAsyncThunk('contentPlanner/deleteStrategy', async ({ strategyId }, { rejectWithValue }) => {
  try {
    await api.deleteStrategy(strategyId);
    return strategyId;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const generateIdeas = createAsyncThunk('contentPlanner/generateIdeas', async ({ strategyId, count, rideTrends }, { rejectWithValue }) => {
  try {
    const res = await api.generateIdeas(strategyId, { Count: count ?? 30, RideTrends: !!rideTrends });
    return { strategyId, ideas: res.data };
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const fetchIdeas = createAsyncThunk('contentPlanner/fetchIdeas', async ({ strategyId, status }, { rejectWithValue }) => {
  try {
    const res = await api.listIdeas(strategyId, status);
    return { strategyId, ideas: res.data };
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const approveIdea = createAsyncThunk('contentPlanner/approveIdea', async ({ ideaId }, { rejectWithValue }) => {
  try {
    const res = await api.approveIdea(ideaId);
    return { ideaId, approval: res.data };
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const skipIdea = createAsyncThunk('contentPlanner/skipIdea', async ({ ideaId }, { rejectWithValue }) => {
  try {
    await api.skipIdea(ideaId);
    return ideaId;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const updateIdea = createAsyncThunk('contentPlanner/updateIdea', async ({ ideaId, data }, { rejectWithValue }) => {
  try {
    const res = await api.updateIdea(ideaId, data);
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

// Slice

const contentPlannerSlice = createSlice({
  name: 'contentPlanner',
  initialState: {
    nichePresets: [],
    strategies: [],
    ideasByStrategy: {}, // { [strategyId]: [idea...] }
    selectedStrategyId: null,
    isLoading: false,
    isGenerating: false,
    error: null,
  },
  reducers: {
    selectStrategy(state, action) {
      state.selectedStrategyId = action.payload;
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Presets
      .addCase(fetchNichePresets.fulfilled, (state, action) => {
        state.nichePresets = action.payload || [];
      })
      // Strategies
      .addCase(fetchStrategies.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchStrategies.fulfilled, (state, action) => {
        state.isLoading = false;
        state.strategies = action.payload || [];
      })
      .addCase(fetchStrategies.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(createStrategy.fulfilled, (state, action) => {
        state.strategies.unshift(action.payload);
        state.selectedStrategyId = action.payload.Id || action.payload.id;
      })
      .addCase(updateStrategy.fulfilled, (state, action) => {
        const updated = action.payload;
        const id = updated.Id ?? updated.id;
        const idx = state.strategies.findIndex((s) => (s.Id ?? s.id) === id);
        if (idx >= 0) state.strategies[idx] = updated;
      })
      .addCase(deleteStrategy.fulfilled, (state, action) => {
        const id = action.payload;
        state.strategies = state.strategies.filter((s) => (s.Id ?? s.id) !== id);
        delete state.ideasByStrategy[id];
        if (state.selectedStrategyId === id) state.selectedStrategyId = null;
      })
      // Ideas
      .addCase(generateIdeas.pending, (state) => {
        state.isGenerating = true;
        state.error = null;
      })
      .addCase(generateIdeas.fulfilled, (state, action) => {
        state.isGenerating = false;
        const { strategyId, ideas } = action.payload;
        const existing = state.ideasByStrategy[strategyId] || [];
        state.ideasByStrategy[strategyId] = [...existing, ...(ideas || [])];
      })
      .addCase(generateIdeas.rejected, (state, action) => {
        state.isGenerating = false;
        state.error = action.payload;
      })
      .addCase(fetchIdeas.fulfilled, (state, action) => {
        const { strategyId, ideas } = action.payload;
        state.ideasByStrategy[strategyId] = ideas || [];
      })
      .addCase(approveIdea.fulfilled, (state, action) => {
        const { ideaId } = action.payload;
        for (const sid of Object.keys(state.ideasByStrategy)) {
          const list = state.ideasByStrategy[sid];
          const idx = list.findIndex((i) => (i.Id ?? i.id) === ideaId);
          if (idx >= 0) list[idx] = { ...list[idx], Status: 3 /* Scheduled */ };
        }
      })
      .addCase(skipIdea.fulfilled, (state, action) => {
        const ideaId = action.payload;
        for (const sid of Object.keys(state.ideasByStrategy)) {
          state.ideasByStrategy[sid] = state.ideasByStrategy[sid].filter((i) => (i.Id ?? i.id) !== ideaId);
        }
      })
      .addCase(updateIdea.fulfilled, (state, action) => {
        const updated = action.payload;
        const id = updated.Id ?? updated.id;
        for (const sid of Object.keys(state.ideasByStrategy)) {
          const list = state.ideasByStrategy[sid];
          const idx = list.findIndex((i) => (i.Id ?? i.id) === id);
          if (idx >= 0) list[idx] = updated;
        }
      });
  },
});

export const { selectStrategy, clearError } = contentPlannerSlice.actions;
export default contentPlannerSlice.reducer;

// Selectors
export const selectContentPlanner = (state) => state.contentPlanner;
export const selectNichePresets = (state) => state.contentPlanner.nichePresets;
export const selectStrategies = (state) => state.contentPlanner.strategies;
export const selectIdeasForStrategy = (strategyId) => (state) => state.contentPlanner.ideasByStrategy[strategyId] || [];
